import { sksisUrls } from '../urls';
import { pgSelect, pgEq } from './postgrest';

interface SoilPitResponse {
  soil_pits_id: string;
}

interface PointFormData {
  filename: string;
  datatype: string;
  loc_source: string;
  locationtype: string;
  description: string;
  longitude: number;
  latitude: number;
}

export class PointService {
  private baseUrl = new sksisUrls().pointDataUrl;
  private imageUrl = new sksisUrls().getImageUrl;
  private publicationsUrl = new sksisUrls().getPublicationsUrl;

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-DreamFactory-Api-Key': new sksisUrls().DefaultXDreamFactoryAPIKey,
    };
  }

  private getImageHeaders() {
    return {
      'Content-Type': 'image/png',
      'X-DreamFactory-Api-Key': new sksisUrls().DefaultXDreamFactoryAPIKey,
    };
  }

  async getImage(filename: string): Promise<Blob> {
    try {
      const response = await fetch(`${this.imageUrl}${filename}?download=true`, {
        headers: this.getImageHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch image');
      return await response.blob();
    } catch (error) {
      console.error('Error fetching image:', error);
      throw error;
    }
  }

  async getPdf(filename: string): Promise<Blob> {
    try {
      const response = await fetch(`${this.publicationsUrl}${filename}?download=true`, {
        headers: this.getImageHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch PDF');
      const blob = await response.blob();
      return new Blob([blob], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error fetching PDF:', error);
      throw error;
    }
  }

  async getSoilPitId(sk_point_id: string): Promise<SoilPitResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${sk_point_id}`, {
        headers: this.getHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch soil pit ID');
      return await response.json();
    } catch (error) {
      console.error('Error fetching soil pit ID:', error);
      throw error;
    }
  }

  async getPolygonComponents(polygonId: string): Promise<any> {
    try {
      // Read-only lookup migrated to PostgREST (data.sksis.ca).
      return await pgSelect('polygon_components', pgEq('poly_id', polygonId));
    } catch (error) {
      console.error('Error fetching polygon components:', error);
      throw error;
    }
  }

  async uploadPoint(formData: PointFormData): Promise<Response> {
    try {
      const nameValuePairs = [
        { name: 'v_filename', value: formData.filename },
        { name: 'v_datatype', value: formData.datatype },
        { name: 'v_loc_source', value: formData.loc_source },
        { name: 'v_loc_type', value: formData.locationtype },
        { name: 'v_uploader', value: localStorage.getItem('email') || '' },
        { name: 'v_description', value: formData.description },
        { name: 'v_long', value: formData.longitude },
        { name: 'v_lat', value: formData.latitude },
      ];

      const body = JSON.stringify({ params: nameValuePairs });
      const response = await fetch(new sksisUrls().uploadPointDataUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        body,
      });
      if (!response.ok) throw new Error('Failed to upload point');
      return response;
    } catch (error) {
      console.error('Error uploading point:', error);
      throw error;
    }
  }

  async deletePoint(id: string): Promise<Response> {
    try {
      const response = await fetch(`${this.baseUrl}${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete point');
      return response;
    } catch (error) {
      console.error('Error deleting point:', error);
      throw error;
    }
  }

  async updatePoint(formData: PointFormData & { id: string }): Promise<Response> {
    try {
      const nameValuePairs = [
        { name: 'v_id', value: formData.id },
        { name: 'v_filename', value: formData.filename },
        { name: 'v_datatype', value: formData.datatype },
        { name: 'v_loc_source', value: formData.loc_source },
        { name: 'v_loc_type', value: formData.locationtype },
        { name: 'v_uploader', value: localStorage.getItem('email') || '' },
        { name: 'v_description', value: formData.description },
        { name: 'v_long', value: formData.longitude },
        { name: 'v_lat', value: formData.latitude },
      ];

      const body = JSON.stringify({ params: nameValuePairs });
      const response = await fetch(new sksisUrls().updatePointDataUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        body,
      });
      if (!response.ok) throw new Error('Failed to update point');
      return response;
    } catch (error) {
      console.error('Error updating point:', error);
      throw error;
    }
  }

  async getSoilPitInfo(soilPitId: string): Promise<any> {
    try {
      const url = `${new sksisUrls().soilPitUrl}/${soilPitId}`;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch soil pit info');
      return await response.json();
    } catch (error) {
      console.error('Error fetching soil pit info:', error);
      throw error;
    }
  }

  async getSoilPitHorizonInfo(soilPitId: string): Promise<any> {
    try {
      const url = `${new sksisUrls().soilPitHorizonUrl}${soilPitId}&order=id%20ASC`;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch soil pit horizon info');
      return await response.json();
    } catch (error) {
      console.error('Error fetching soil pit horizon info:', error);
      throw error;
    }
  }
}

export const pointService = new PointService();
